import { ChangeDetectionStrategy, Component, computed, forwardRef, input, model, signal } from '@angular/core';
import { NG_VALUE_ACCESSOR, type ControlValueAccessor } from '@angular/forms';

import type { EsnTextFieldInputType, EsnTextFieldKind } from '../../../shared/types/forms';
import { IconComponent } from '../../atoms/icon/icon.component';

let nextId = 0;

/**
 * Labelled text entry for console forms: a single-line input or an auto-growing notes area,
 * with helper text and an error message. Works with `[(value)]`, `[(ngModel)]` and reactive forms.
 */
@Component({
  selector: 'esn-text-field',
  imports: [IconComponent],
  templateUrl: './text-field.component.html',
  styleUrl: './text-field.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => TextFieldComponent), multi: true }],
})
export class TextFieldComponent implements ControlValueAccessor {
  readonly value = model('');
  readonly kind = input<EsnTextFieldKind>('single');
  readonly type = input<EsnTextFieldInputType>('text');
  readonly label = input('');
  readonly placeholder = input('');
  readonly hint = input('');

  /**
   * A message shows it beneath the field; `true` marks the field invalid without a message.
   */
  readonly error = input<string | boolean | null>(null);
  readonly disabled = input(false);
  readonly readonly = input(false);
  readonly required = input(false);
  readonly name = input<string | null>(null);
  readonly autocomplete = input<string | null>(null);
  readonly maxlength = input<number | null>(null);

  /**
   * Accessible name of the password reveal toggle.
   */
  readonly revealLabel = input('Show password');

  protected readonly id = `esn-text-field-${nextId++}`;
  protected readonly messageId = `${this.id}-message`;
  protected readonly revealed = signal(false);
  private readonly formDisabled = signal(false);

  protected readonly isDisabled = computed(() => this.disabled() || this.formDisabled());
  protected readonly invalid = computed(() => !!this.error());
  protected readonly message = computed(() => {
    const error = this.error();
    return typeof error === 'string' && error ? error : this.hint();
  });
  protected readonly nativeType = computed(() =>
    this.type() === 'password' && this.revealed() ? 'text' : this.type());

  private onChange: (value: string) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  protected handleInput(event: Event): void {
    const next = (event.target as HTMLInputElement | HTMLTextAreaElement).value;
    this.value.set(next);
    this.onChange(next);
  }

  protected handleBlur(): void {
    this.onTouched();
  }

  protected toggleReveal(): void {
    this.revealed.update((shown) => !shown);
  }

  writeValue(value: string | null): void {
    this.value.set(value ?? '');
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean): void {
    this.formDisabled.set(disabled);
  }
}
